import { z } from 'zod';
import { CLIENT_TYPES } from './enums.js';

/**
 * External client databases (Supabase projects of the group's B2B / B2C businesses). Their customers flow into the
 * portal's Clients one way (Supabase → portal) through Database Webhooks, plus an on-demand backfill.
 */

/** Header Supabase must send with every webhook call (added as a custom HTTP header on the Database Webhook). */
export const CLIENT_SOURCE_SECRET_HEADER = 'x-ff-webhook-secret';

/** Portal fields a source column can be mapped to, in the order shown in the mapping form. */
export const CLIENT_SOURCE_FIELDS = [
  'externalId',
  'fullName',
  'firstName',
  'lastName',
  'email',
  'phone',
  'whatsapp',
  'country',
  'addressLine1',
  'addressLine2',
  'city',
  'state',
  'postalCode',
  'countryCode',
  'notes',
] as const;
export type ClientSourceField = (typeof CLIENT_SOURCE_FIELDS)[number];

export const CLIENT_SOURCE_FIELD_LABELS: Record<ClientSourceField, string> = {
  externalId: 'External ID (unique key)',
  fullName: 'Full name',
  firstName: 'First name (if no full name)',
  lastName: 'Last name (if no full name)',
  email: 'Email',
  phone: 'Phone',
  whatsapp: 'WhatsApp',
  country: 'Country',
  addressLine1: 'Address line 1',
  addressLine2: 'Address line 2',
  city: 'City',
  state: 'State / province',
  postalCode: 'Postal code',
  countryCode: 'Country code (ISO 2 letters)',
  notes: 'Notes',
};

/** A source column: a plain identifier, or a dotted path into a JSON column (e.g. metadata.phone). */
const column = z
  .string()
  .trim()
  .max(120)
  // Only used to read values from a row (never put in a query), so names with spaces or dashes are fine too.
  .regex(/^[^.\s][^.]*(\.[^.\s][^.]*)*$/, 'Use a column name such as full_name (or json_column.key)');

const mappingShape = Object.fromEntries(CLIENT_SOURCE_FIELDS.map((f) => [f, column.or(z.literal('')).optional()])) as Record<
  ClientSourceField,
  z.ZodOptional<z.ZodUnion<[typeof column, z.ZodLiteral<''>]>>
>;

/** Portal field → source column. Blank entries are dropped; the external ID defaults to the "id" column. */
export const clientSourceMapping = z
  .object(mappingShape)
  .transform((m) => {
    const out = Object.fromEntries(Object.entries(m).filter(([, v]) => !!v)) as Partial<Record<ClientSourceField, string>>;
    out.externalId ??= 'id';
    return out;
  })
  .refine((m) => !!m.fullName || !!m.firstName || !!m.lastName || !!m.email, { message: 'Map at least the full name (or first / last name) or the email' });
export type ClientSourceMapping = z.infer<typeof clientSourceMapping>;

/**
 * The project URL as people paste it: https://<ref>.supabase.co with or without a path (e.g. /rest/v1/), a dashboard
 * link (supabase.com/dashboard/project/<ref>/…), the bare host, or just the project ref.
 */
export function normalizeProjectUrl(raw: string) {
  const v = raw.trim().toLowerCase();
  const dash = v.match(/supabase\.com\/dashboard\/project\/([a-z0-9-]+)/);
  if (dash) return `https://${dash[1]}.supabase.co`;
  const host = v.replace(/^[a-z]+:\/\//, '').split(/[/?#]/)[0];
  if (/^[a-z0-9][a-z0-9-]{2,62}$/.test(host)) return `https://${host}.supabase.co`;
  return `https://${host}`;
}

export const SUPABASE_PROJECT_URL = /^https:\/\/[a-z0-9][a-z0-9-]{2,62}\.supabase\.(co|in)$/;
const identifier = z.string().trim().min(1).max(63).regex(/^[A-Za-z_][A-Za-z0-9_]*$/, 'Letters, digits and _ only');

export const clientSourceInput = z.object({
  name: z.string().trim().min(2).max(120),
  projectUrl: z
    .string()
    .trim()
    .transform(normalizeProjectUrl)
    .pipe(z.string().regex(SUPABASE_PROJECT_URL, 'Use the project URL, e.g. https://abcdefghijklmnop.supabase.co')),
  schemaName: identifier.default('public'),
  tableName: identifier.default('customers'),
  clientType: z.enum(CLIENT_TYPES),
  sellerId: z.string().uuid().nullish(),
  mapping: clientSourceMapping.default({ externalId: 'id', fullName: 'full_name', email: 'email', phone: 'phone' }),
  enabled: z.boolean().default(true),
  /** Write-only. Blank on edit keeps the saved key. */
  serviceRoleKey: z.string().trim().max(2000).optional(),
});
export type ClientSourceInput = z.infer<typeof clientSourceInput>;

export const clientSourceTestInput = z.object({
  sourceId: z.string().uuid().optional(),
  projectUrl: clientSourceInput.shape.projectUrl.optional(),
  schemaName: identifier.optional(),
  tableName: identifier.optional(),
  serviceRoleKey: z.string().trim().max(2000).optional(),
});

export const CLIENT_SOURCE_EVENT_STATUSES = ['created', 'linked', 'updated', 'unchanged', 'removed', 'ignored', 'failed', 'rejected', 'backfill'] as const;
export type ClientSourceEventStatus = (typeof CLIENT_SOURCE_EVENT_STATUSES)[number];

/** How a client record was first created (set on create; backfilled from the audit log for older clients). */
export const CLIENT_CREATED_VIA = ['manual', 'import', 'supabase', 'other'] as const;
export type ClientCreatedVia = (typeof CLIENT_CREATED_VIA)[number];
export const CLIENT_CREATED_VIA_LABELS: Record<ClientCreatedVia, string> = { manual: 'Manual', import: 'Excel import', supabase: 'Supabase', other: 'Other / unknown' };

/**
 * Clients list "origin" filter: linked to a client database (Supabase, any source), not linked (Excel / manual), or
 * not linked and created by an Excel import / by hand / otherwise.
 */
export const CLIENT_ORIGIN_FILTERS = ['supabase', 'not_supabase', 'import', 'manual', 'other'] as const;
export type ClientOriginFilter = (typeof CLIENT_ORIGIN_FILTERS)[number];

export const clientBulkDeleteInput = z.object({
  clientIds: z.array(z.string().uuid()).min(1).max(500),
  reason: z.string().trim().min(3, 'Give a reason').max(500),
  /** Also delete clients linked to a client database (Supabase). Default: they are skipped and reported. */
  includeSupabase: z.boolean().default(false),
});

export const clientMergeInput = z.object({
  /** The client kept (usually the Supabase one). */
  keepId: z.string().uuid(),
  /** The duplicate: everything it has moves to the kept client, then it is deleted. */
  removeId: z.string().uuid(),
  reason: z.string().trim().max(500).optional(),
});

export const clientMergeBulkInput = z.object({
  pairs: z.array(clientMergeInput.pick({ keepId: true, removeId: true })).min(1).max(500),
  reason: z.string().trim().max(500).optional(),
});

export const clientMergeHighInput = z.object({
  reason: z.string().trim().max(500).optional(),
  /** Only list the merges that would be made. */
  dryRun: z.boolean().default(false),
});

/** Why two clients look like the same person (see the Find duplicates page). */
export const DUPLICATE_REASONS = ['email', 'phone', 'name', 'address', 'country'] as const;
export type DuplicateReason = (typeof DUPLICATE_REASONS)[number];
export const DUPLICATE_REASON_LABELS: Record<DuplicateReason, string> = { email: 'Same email', phone: 'Same phone / WhatsApp', name: 'Same name', address: 'Same assigned address', country: 'Same country' };
/** high: same email or phone (can be merged in bulk) · medium: same name + same assigned address · low: same name only. */
export const DUPLICATE_CONFIDENCES = ['high', 'medium', 'low'] as const;
export type DuplicateConfidence = (typeof DUPLICATE_CONFIDENCES)[number];
